import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';

import { Box, useIntersectionObserver } from '@packages/shared';
import { ProductCard } from '@packages/modules';
import { useIsBot } from '@packages/tracking';
import type { ProductCardSpecificProps } from '@packages/modules/src/ProductCard/types';
import { rememberPageData } from '@packages/shared/src/hooks/useScrollRestoration';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { getHeightHeuristic } from './heightHeuristic';
import { useProductImpressionTracking } from './tracking/useProductImpressionTracking';
import { useProductClickTracking } from './tracking/useProductClickTracking';
import { buildGlycerinTrackingData } from './tracking/buildGlycerinTrackingData';
import {
  SearchProductCardItemFragment,
  useVariationDataForProductCard,
} from './useVariationDataForProductCard';
import { useDisplayMode } from '../../hooks/useDisplayMode';

export const SearchProductCardResultFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardResultFragment on SearchProductResult {
    id
    ...SearchProductCardImpressionTrackingResultFragment
    ...SearchProductCardClickTrackingResultFragment
    ...SearchProductCardGlycerinTrackingResultFragment
    ...UseDisplayModeFragment
  }
`);

export const SearchProductCardVariationDataDocument = graphql(/* GraphQL */ `
  query SearchProductCardVariationData($resultId: ID!, $styleId: ID!) {
    styleDetails(resultId: $resultId, styleId: $styleId) {
      variationGroups {
        ...SearchProductCardFullVariationGroupFragment
        ...SearchProductCardClickTrackingVariationGroupFragment
        ...SearchProductCardGlycerinTrackingVariationGroupFragment
      }
    }
  }
`);

export type ActualSearchProductCardProps = {
  maskedItem: FragmentType<typeof SearchProductCardItemFragment>;
  maskedResult: FragmentType<typeof SearchProductCardResultFragment>;
} & Pick<
  ProductCardSpecificProps,
  'priority' | 'useAdditionalFlagsSpacer' | 'useRatingsSpacer' | 'isSponsored'
> & {
    wishListTrackingProps?: Omit<ProductCardSpecificProps['wishListTrackingProps'], 'product'>;
  } & Pick<ProductCardSpecificProps['price'], 'useUnitPriceSpacer'>;

/**
 * A search-page-specific wrapper around `ProductCard`
 */
const ActualSearchProductCard: FC<ActualSearchProductCardProps> = (props) => {
  // local state to allow changing colors, also used for fetching full product data
  const [activeVariationIndex, setActiveVariationIndex] = useState(0);

  const {
    maskedItem,
    maskedResult,
    useUnitPriceSpacer,
    wishListTrackingProps,
    ...passthroughProps
  } = props;

  const result = getFragmentData(SearchProductCardResultFragment, maskedResult);
  const item = getFragmentData(SearchProductCardItemFragment, maskedItem);

  const [{ data, error }] = useQuery({
    query: SearchProductCardVariationDataDocument,
    variables: {
      resultId: result.id,
      styleId: item.styleId,
    },
    pause: activeVariationIndex === 0,
    context: useMemo(() => ({ suspense: false }), []),
  });

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  const maskedActiveVariation =
    data?.styleDetails?.variationGroups?.[activeVariationIndex] ?? item.primaryVariationGroup;

  const { dispatchProductClickTracking } = useProductClickTracking(
    result,
    item,
    maskedActiveVariation,
  );

  const { clickTrackingProps, wishListTrackingProduct } = buildGlycerinTrackingData(
    result,
    item,
    maskedActiveVariation,
  );

  const { displayMode } = useDisplayMode(result);

  const activeVariationProps = useVariationDataForProductCard(
    maskedItem,
    activeVariationIndex,
    maskedActiveVariation,
    displayMode,
    useUnitPriceSpacer,
  );

  const { asPath } = useRouter();

  const handleClick = () => {
    dispatchProductClickTracking();
    rememberPageData(item.styleId, asPath);
  };

  if (error) {
    // Handle error state
    return <div>Error loading data</div>;
  }

  return (
    <ProductCard
      {...passthroughProps}
      {...activeVariationProps}
      clickTrackingProps={clickTrackingProps}
      wishListTrackingProps={{
        ...wishListTrackingProps,
        product: wishListTrackingProduct,
      }}
      shrinkToFit
      onColorChange={setActiveVariationIndex}
      onProductLinkActionAreaClick={handleClick}
    />
  );
};

export type SearchProductCardProps = ActualSearchProductCardProps & {
  alwaysRender?: boolean;
  onFirstBecameVisible?: () => void;
};

/**
 * Specialized version of `ProductCard` for the search product grids
 */
export const SearchProductCard: FC<SearchProductCardProps> = (props) => {
  const { alwaysRender, onFirstBecameVisible, ...actualSearchProductCardProps } = props;

  const { maskedItem, maskedResult } = actualSearchProductCardProps;

  const item = getFragmentData(SearchProductCardItemFragment, maskedItem);
  const result = getFragmentData(SearchProductCardResultFragment, maskedResult);

  const { dispatchProductImpressionTracking } = useProductImpressionTracking(result, item);

  const wrapper = useRef<HTMLDivElement>(null);

  // check if the card is fully visible
  useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting) return;
      // intersectOnce is set to true, so this callback will only be called once
      onFirstBecameVisible?.();
      dispatchProductImpressionTracking();
    },
  });

  // check if the card will scroll into view soon (as defined by the margin)
  const { isIntersecting: shouldRender } = useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 0,
    rootMargin: '500px 0px 500px 0px',
  });

  const isBot = useIsBot();

  const showActualCard = alwaysRender || shouldRender || isBot;

  const { styleId, styleName } = item;

  const { displayMode } = useDisplayMode(result);

  return (
    <Box
      ref={wrapper}
      sx={{ height: showActualCard ? '100%' : getHeightHeuristic(props, displayMode) }}
      id={styleId}
    >
      {showActualCard ? <ActualSearchProductCard {...actualSearchProductCardProps} /> : styleName}
    </Box>
  );
};
